<script setup lang="ts">
import { ref } from 'vue';

const emit = defineEmits(['completed', 'error']);

const step = ref<'upload' | 'processing' | 'completed'>('upload');
const uploadedDocuments = ref({
  passport: false,
  selfie: false,
  addressProof: false
});

function handleFileUpload(docType: 'passport' | 'selfie' | 'addressProof') {
  uploadedDocuments.value[docType] = true;
}

function simulateVerification() {
  step.value = 'processing';

  // Simulate processing time
  setTimeout(() => {
    step.value = 'completed';
    emit('completed', {
      status: 'approved',
      reviewAnswer: 'GREEN',
      reviewResult: {
        reviewAnswer: 'GREEN'
      }
    });
  }, 3000);
}

const allDocumentsUploaded = () => {
  return uploadedDocuments.value.passport &&
         uploadedDocuments.value.selfie &&
         uploadedDocuments.value.addressProof;
};
</script>

<template>
  <div class="mock-verification w-full p-4 md:p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
    <div class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
      <p class="text-sm text-yellow-800 dark:text-yellow-200 font-semibold">
        🧪 Mock Verification Mode (Development Only)
      </p>
      <p class="text-xs text-yellow-600 dark:text-yellow-300 mt-1">
        This is a simulated verification interface for testing purposes
      </p>
    </div>

    <!-- Upload Step -->
    <div v-if="step === 'upload'" class="space-y-6">
      <h3 class="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
        Identity Verification
      </h3>

      <p class="text-gray-600 dark:text-gray-300 mb-6">
        Please upload the following documents to verify your identity:
      </p>

      <!-- Document Upload Cards -->
      <div class="space-y-4">
        <!-- Passport -->
        <div class="border-2 border-dashed rounded-lg p-4 md:p-6 transition-all"
             :class="uploadedDocuments.passport
               ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
               : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="flex items-center gap-3 md:gap-4">
              <div class="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   :class="uploadedDocuments.passport ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'">
                <span class="text-xl md:text-2xl">{{ uploadedDocuments.passport ? '✓' : '📄' }}</span>
              </div>
              <div>
                <h4 class="font-semibold text-sm md:text-base text-gray-800 dark:text-gray-100">
                  {{ uploadedDocuments.passport ? 'Passport Uploaded' : 'Passport or ID Card' }}
                </h4>
                <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  Upload a clear photo of your passport or national ID
                </p>
              </div>
            </div>
            <button
              v-if="!uploadedDocuments.passport"
              @click="handleFileUpload('passport')"
              class="w-full md:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm md:text-base">
              Upload
            </button>
          </div>
        </div>

        <!-- Selfie -->
        <div class="border-2 border-dashed rounded-lg p-4 md:p-6 transition-all"
             :class="uploadedDocuments.selfie
               ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
               : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="flex items-center gap-3 md:gap-4">
              <div class="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   :class="uploadedDocuments.selfie ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'">
                <span class="text-xl md:text-2xl">{{ uploadedDocuments.selfie ? '✓' : '🤳' }}</span>
              </div>
              <div>
                <h4 class="font-semibold text-sm md:text-base text-gray-800 dark:text-gray-100">
                  {{ uploadedDocuments.selfie ? 'Selfie Uploaded' : 'Selfie Photo' }}
                </h4>
                <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  Take a selfie holding your ID document
                </p>
              </div>
            </div>
            <button
              v-if="!uploadedDocuments.selfie"
              @click="handleFileUpload('selfie')"
              class="w-full md:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm md:text-base">
              Upload
            </button>
          </div>
        </div>

        <!-- Proof of Address -->
        <div class="border-2 border-dashed rounded-lg p-4 md:p-6 transition-all"
             :class="uploadedDocuments.addressProof
               ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
               : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="flex items-center gap-3 md:gap-4">
              <div class="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   :class="uploadedDocuments.addressProof ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'">
                <span class="text-xl md:text-2xl">{{ uploadedDocuments.addressProof ? '✓' : '🏠' }}</span>
              </div>
              <div>
                <h4 class="font-semibold text-sm md:text-base text-gray-800 dark:text-gray-100">
                  {{ uploadedDocuments.addressProof ? 'Proof of Address Uploaded' : 'Proof of Address' }}
                </h4>
                <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                  Utility bill, bank statement, or rental agreement
                </p>
              </div>
            </div>
            <button
              v-if="!uploadedDocuments.addressProof"
              @click="handleFileUpload('addressProof')"
              class="w-full md:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm md:text-base">
              Upload
            </button>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          @click="simulateVerification"
          :disabled="!allDocumentsUploaded()"
          class="w-full py-3 rounded-lg font-semibold transition-all"
          :class="allDocumentsUploaded()
            ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'">
          {{ allDocumentsUploaded() ? 'Submit for Verification' : 'Upload all documents to continue' }}
        </button>
      </div>
    </div>

    <!-- Processing Step -->
    <div v-if="step === 'processing'" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-4"></div>
      <h3 class="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
        Verifying Your Documents
      </h3>
      <p class="text-gray-600 dark:text-gray-400">
        This usually takes a few moments...
      </p>
      <div class="mt-6 space-y-2">
        <div class="flex items-center justify-center gap-2 text-sm text-gray-500">
          <span class="animate-pulse">●</span>
          Checking document authenticity
        </div>
        <div class="flex items-center justify-center gap-2 text-sm text-gray-500">
          <span class="animate-pulse animation-delay-300">●</span>
          Matching facial features
        </div>
        <div class="flex items-center justify-center gap-2 text-sm text-gray-500">
          <span class="animate-pulse animation-delay-600">●</span>
          Verifying address details
        </div>
      </div>
    </div>

    <!-- Completed Step -->
    <div v-if="step === 'completed'" class="text-center py-12">
      <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
        <span class="text-5xl">✓</span>
      </div>
      <h3 class="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
        Verification Complete!
      </h3>
      <p class="text-gray-600 dark:text-gray-400 mb-6">
        Your identity has been successfully verified
      </p>
      <div class="inline-block px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <p class="text-sm text-green-700 dark:text-green-300 font-medium">
          Status: Approved ✓
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animation-delay-300 {
  animation-delay: 0.3s;
}

.animation-delay-600 {
  animation-delay: 0.6s;
}
</style>
